import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';

/**
 * *Now there* on the summary's Timezone card (`twin-details-page-summary`, R5.1): the time in the
 * twin's zone, `HH:mm · {abbreviation}` (`15:24 · BST`), moving on each minute ON the minute.
 *
 * The abbreviation is the runtime's short zone name. British English first, since the people using
 * the wizard read *BST*; where that gives only an offset (`GMT-4` for New York) and US English names
 * the zone (`EDT`), that name; otherwise the offset itself (`GMT+4`), which R5.1 asks for when there
 * is no abbreviation.
 *
 * `null` for no zone. A zone the runtime does not know reads as the zone alone, with no time (spec,
 * Failure States). Nothing here reads the browser's own zone.
 */

const BARE_OFFSET = /^(GMT|UTC)([+-]|$)/;

/**
 * Runtimes differ on a zero offset: Node's ICU names London in winter `GMT`, while the system
 * Chromium's says `GMT+0` (measured 2026-10-26). Both mean GMT. Exported for its test.
 */
export const tidyZoneName = (name: string): string => name.replace(/^(GMT|UTC)[+-]0(:00)?$/, '$1');

/** The zone's short name at `at`, or its `GMT±h` offset. Exported for its test. */
export const zoneAbbreviation = (at: DateTime): string => {
  const gb = tidyZoneName(at.setLocale('en-GB').toFormat('ZZZZ'));
  if (!BARE_OFFSET.test(gb) || gb === 'UTC' || gb === 'GMT') return gb;
  const us = tidyZoneName(at.setLocale('en-US').toFormat('ZZZZ'));
  return BARE_OFFSET.test(us) ? gb : us;
};

/** The row's text for `zone` at the current time. */
export const nowInZoneText = (zone: string): string => {
  const at = DateTime.now().setZone(zone);
  if (!at.isValid) return zone;
  return `${at.toFormat('HH:mm')} · ${zoneAbbreviation(at)}`;
};

const MINUTE_MS = 60_000;

export const useNowInZone = (zone: string | null): string | null => {
  const kept = zone?.trim() || null;
  const [text, setText] = useState<string | null>(() => (kept ? nowInZoneText(kept) : null));

  useEffect(() => {
    if (!kept) {
      setText(null);
      return undefined;
    }
    // A changed zone reads at once, then on each minute boundary.
    setText(nowInZoneText(kept));
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      timer = setTimeout(
        () => {
          setText(nowInZoneText(kept));
          arm();
        },
        MINUTE_MS - (Date.now() % MINUTE_MS),
      );
    };
    arm();
    return () => clearTimeout(timer);
  }, [kept]);

  return text;
};
